import { ReactNode } from 'react'
import { useAppSelector } from '../../../../redux/hooks.ts'
import { selectThemeStyle } from '../../../../features/setting/setting.slice.ts'
import IdeyaTypography from '../../../../common/antd/ideya-typography.tsx'
import Row from 'antd/es/grid/row'
import Col from 'antd/es/grid/col'
import { convertPxToNumber } from '../../../../utils/helpers.ts'
import { useFixedBreakpoint } from '../../../../common/hooks/use-is-mobile.ts'

function chunkRows<T>(arr: T[], size: number): T[][] {
  const result: T[][] = []
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size))
  }
  return result
}

interface IdeaSummaryCardProps {
  title: string
  children?: ReactNode
  slotComponent?: ReactNode
  textComponent?: ReactNode
  rows?: { label: string; value: ReactNode }[]
  ref?: React.Ref<HTMLDivElement>
  overlayMsg?: string | ReactNode
}

export const IdeaSummaryCard: React.FC<IdeaSummaryCardProps> = ({
  title,
  children,
  slotComponent,
  textComponent,
  rows,
  ref,
  overlayMsg,
}) => {
  const themeStyle = useAppSelector(selectThemeStyle)
  const screens = useFixedBreakpoint()
  const isMobile = !screens.md

  return (
    <div
      ref={ref}
      style={{
        padding: `${themeStyle.marginXS} ${themeStyle.marginBase}`,
        textAlign: 'left',
        gap: themeStyle.marginXS,
        flex: '1 0 0',
        borderRadius: themeStyle.borderRadiusLG,
        border: `1px solid ${themeStyle.sumCardOutline}`,
        background: themeStyle.sumCardBg,
        boxShadow: themeStyle.sumCardBoxShadow,
        backdropFilter: 'blur(4px)',
        height: '8.5rem',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
      }}
    >
      <IdeyaTypography.Text
        type={'secondary'}
        style={{
          display: 'inline-block',
          height: '1.125rem',
        }}
      >
        {title}
      </IdeyaTypography.Text>
      {overlayMsg && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%',
          }}
        >
          {overlayMsg}
        </div>
      )}
      {!overlayMsg && (
        <>
          <div style={{ flex: '1 1 0' }}>
            <Row
              justify={'space-between'}
              align={'middle'}
              style={{ height: '100%' }}
            >
              <Col md={slotComponent ? 16 : 24}>
                {textComponent && <div>{textComponent}</div>}
                {children && <div>{children}</div>}
              </Col>
              {slotComponent && (
                <Col
                  md={8}
                  style={{ marginLeft: isMobile ? themeStyle.marginSM : '' }}
                >
                  {slotComponent}
                </Col>
              )}
            </Row>
          </div>
          {rows && (
            <div
              style={{
                borderTop: `1px solid ${themeStyle.colorSplit}`,
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-end',
                flexWrap: 'wrap',
                flex: 0,
                gap: '.25rem',
                paddingTop: themeStyle.marginXXS,
              }}
            >
              {chunkRows(rows, 2).map((pair, idx) => {
                const firstColSpan = pair[1] ? 12 : 24
                return (
                  <div
                    key={idx}
                    style={{
                      width: '100%',
                    }}
                  >
                    <Row gutter={[convertPxToNumber(themeStyle.marginMD), 0]}>
                      <IdeyaRowInfo
                        label={pair[0]?.label}
                        value={pair[0]?.value}
                        colSpan={firstColSpan}
                      />
                      {pair[1] && (
                        <IdeyaRowInfo
                          label={pair[1]?.label}
                          value={pair[1]?.value}
                        />
                      )}
                    </Row>
                  </div>
                )
              })}
            </div>
          )}
        </>
      )}
    </div>
  )
}

const IdeyaRowInfo: React.FC<{
  value: ReactNode
  label?: string
  colSpan?: number
}> = ({
  value,
  label,
  colSpan = 12,
}: {
  value: ReactNode
  label?: string
  colSpan?: number
}) => {
  return (
    <Col span={colSpan}>
      <Row
        justify={'space-between'}
        style={{ height: '1.125rem', flexWrap: 'nowrap', overflow: 'hidden' }}
      >
        <Row style={{ flexShrink: 0 }}>
          <IdeyaTypography.Text type={'secondary'} size={'sm'}>
            {label}
          </IdeyaTypography.Text>
        </Row>
        <Row style={{ overflow: 'hidden' }}>
          <IdeyaTypography.Text size={'sm'} style={{ whiteSpace: 'nowrap' }}>
            {value}
          </IdeyaTypography.Text>
        </Row>
      </Row>
    </Col>
  )
}
